import { configureSharedCookieAuth } from "@assetwatch/auth";
import { configureChartsRuntime } from "@assetwatch/charts";
import "@assetwatch/charts/styles.css";
import * as Sentry from "@sentry/react";
import { RouterProvider } from "@tanstack/react-router";
import { Amplify } from "aws-amplify";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";
import { AppErrorFallback } from "./components/AppErrorFallback";
import config from "./config";
import { initMlFrontendSentry } from "./lib/sentry";
import { queryClient, router } from "./router";

initMlFrontendSentry();

// Configure SciChart once before any @assetwatch/charts surface mounts. WASM is
// served from /public (copied by scripts/copy-scichart-wasm.mjs); dev uses the
// community license, deployed envs use the runtime license key.
configureChartsRuntime({
  licenseKey: import.meta.env.VITE_SCICHART_LICENSE_KEY,
  wasmBaseUrl: "/",
  useCommunityLicense: import.meta.env.DEV,
});

const authApiBaseUrl =
  (import.meta.env.VITE_AUTH_API_BASE_URL as string | undefined)?.replace(
    /\/$/,
    "",
  ) ?? "";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.cognito.USER_POOL_ID,
      identityPoolId: config.cognito.IDENTITY_POOL_ID,
      userPoolClientId: config.cognito.APP_CLIENT_ID,
      loginWith: {
        email: true,
        oauth: {
          domain: config.cognito.DOMAIN,
          scopes: [
            "email",
            "openid",
            "aws.cognito.signin.user.admin",
            "profile",
          ],
          redirectSignIn: [`${window.location.origin}/`],
          redirectSignOut: [`${window.location.origin}/`],
          responseType: "code",
        },
      },
    },
  },
  API: {
    REST: {
      ML_API: {
        endpoint: config.mlApi.baseUrl,
        region: "us-east-2",
      },
      ...(authApiBaseUrl
        ? {
            apiVeroUser: {
              endpoint: `${authApiBaseUrl}/user`,
              region: "us-east-2",
            },
          }
        : {}),
    },
  },
});

configureSharedCookieAuth(import.meta.env.VITE_DEPLOY_ENVIRONMENT);

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

createRoot(rootElement).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<AppErrorFallback />}>
      <RouterProvider router={router} context={{ queryClient }} />
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
